/**
 * @fileoverview Lazy, Arrow-backed row accessor for rift query results.
 *
 * A rift result is kept as its columnar Arrow Table (roughly its wire size)
 * instead of being eagerly expanded into one JS object per row (a 10-20x
 * memory blow-up). `RiftData` materialises JS values on demand for only the
 * cells a consumer actually reads — the rendered virtual window in the table,
 * the (small) pre-aggregated chart rows, or a CSV export batch.
 *
 * The temporal / NaN-null conversions that previously ran eagerly in the
 * DuckDB worker now live in {@link createRiftData}'s cell accessor.
 */

import type { Table as ArrowTable, Vector } from "apache-arrow";

import { buildCellConverter, normalizeCell } from "~/libs/arrow-cell";

/** Lazy accessor over an Arrow result table. */
export type RiftData = {
  /** Total number of rows in the underlying Arrow Table. */
  numRows: number;
  /**
   * Reads a single cell, applying the standard conversions:
   * - element access preserves nulls (unlike `Vector.toArray()`),
   * - temporal columns yield numbers rather than bigint,
   * - a genuine `NaN` numeric value normalises to `null`.
   * Returns `undefined` for an unknown column.
   */
  getCell(rowIndex: number, column: string): unknown;
  /**
   * Materialises plain row objects for the half-open range `[start, end)`.
   * Defaults to the whole table. Used by consumers that need row objects
   * (charts, CSV export batches, tests) rather than windowed cell reads.
   */
  toRows(start?: number, end?: number): Record<string, unknown>[];
};

/**
 * Wraps an Arrow `Table` in a {@link RiftData} lazy accessor. Column vectors
 * and per-column converters are resolved once up front; cell reads use
 * `Vector.get(i)`, whose visitor path preserves nulls and yields numbers for
 * temporal columns. Decimal columns (DuckDB HUGEINT / DECIMAL) are converted
 * to scale-aware numbers so downstream consumers (charts, formatters) receive
 * plain numbers rather than `DecimalBigNum` objects.
 */
export const createRiftData = (table: ArrowTable): RiftData => {
  const vectors = new Map<string, Vector>();
  const converters = new Map<string, (value: unknown) => unknown>();
  const columnNames: string[] = [];
  for (const field of table.schema.fields) {
    columnNames.push(field.name);
    const child = table.getChild(field.name);
    if (child) vectors.set(field.name, child);
    converters.set(field.name, buildCellConverter(field.type));
  }
  const numRows = table.numRows;

  const getCell = (rowIndex: number, column: string): unknown => {
    const vector = vectors.get(column);
    if (!vector) return undefined;
    const convert = converters.get(column) ?? normalizeCell;
    return convert(vector.get(rowIndex));
  };

  const toRows = (start = 0, end = numRows): Record<string, unknown>[] => {
    const from = Math.max(0, start);
    const to = Math.min(numRows, end);
    const rows: Record<string, unknown>[] = [];
    for (let i = from; i < to; i++) {
      const row: Record<string, unknown> = {};
      for (const name of columnNames) {
        const vector = vectors.get(name);
        if (!vector) {
          row[name] = undefined;
          continue;
        }
        const convert = converters.get(name) ?? normalizeCell;
        row[name] = convert(vector.get(i));
      }
      rows.push(row);
    }
    return rows;
  };

  return { numRows, getCell, toRows };
};
